import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info } from 'lucide-react';
import { splitIntoDeliveries, getFullLoad, MIN_LOAD } from '../utils/loadSplit';
/**
 * Shared "small-load minimum" warning. Renders the same yellow info card the
 * Hero estimator shows when a multi-delivery order's partial last load gets
 * billed up to the 5yd minimum.
 *
 * Returns null when no adjustment applies (single load, or last load already
 * ≥ 5yd), so callers can drop this in without conditional wrappers.
 */
export function SmallLoadBreakdown({
  quantity,
  productName,
  className = ''
}: {
  quantity: number;
  productName: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!quantity || quantity <= 0) return null;
  const fullLoad = getFullLoad(productName);
  if (quantity <= fullLoad) return null;
  const loads = splitIntoDeliveries(quantity, productName);
  const deliveries = loads.length;
  if (deliveries <= 1) return null;
  const lastLoadActual = quantity - (deliveries - 1) * fullLoad;
  if (lastLoadActual >= MIN_LOAD) return null;
  const billable = (deliveries - 1) * fullLoad + MIN_LOAD;
  return <div className={className} data-id="element-1563">
      <button type="button" onClick={() => setOpen(v => !v)} className="inline-flex items-center gap-1.5 text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={open} data-id="element-1564">
        <Info className="w-3.5 h-3.5" data-id="element-1565" />
        You asked for {quantity} yds — billed for {billable} yds. Why?
      </button>
      <AnimatePresence data-id="element-1566">
        {open && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-1567">
            <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-1568">
              <p data-id="element-1569">
                <span className="font-bold" data-id="element-1570">5-yard minimum per delivery.</span>{' '}
                Your order splits into {deliveries} loads:
              </p>
              <ul className="list-disc list-inside space-y-0.5 text-stone-600 dark:text-white/70" data-id="element-1571">
                {Array.from({
              length: deliveries - 1
            }).map((_, i) => <li key={i} data-id="element-1572">
                    Load {i + 1}: {fullLoad} yds (full)
                  </li>)}
                <li data-id="element-1573">
                  Load {deliveries}: {lastLoadActual} yd needed → billed as{' '}
                  {MIN_LOAD} yds (minimum)
                </li>
              </ul>
              <p className="pt-1" data-id="element-1574">
                Trucks have fixed dispatch costs, so each delivery is billed at{' '}
                {MIN_LOAD} yds even if you need less. Round up your project to
                fill the truck and you'll get more material at no extra cost.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}